import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import {
  Plus,
  Users,
  Loader2,
  Copy,
  Check,
  ChevronRight,
  Church
} from "lucide-react";
import { apiClient } from "../lib/apiClient";


export function GroupsSidebar({ groups, churches, onDataRefresh }) {
  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: "", description: "", church_id: "" });
  const [inviteCode, setInviteCode] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const createGroup = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        name: groupForm.name,
        description: groupForm.description || null,
        church_id: groupForm.church_id || null
      };
      await apiClient.post("groups", payload);
      setGroupForm({ name: "", description: "", church_id: "" });
      setNewGroupOpen(false);
      onDataRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to create group");
    } finally {
      setFormLoading(false);
    }
  };

  const joinGroup = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await apiClient.post("groupJoin", inviteCode, {});
      setInviteCode("");
      setJoinGroupOpen(false);
      onDataRefresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to join group");
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2">
        <h2 className="font-serif text-lg sm:text-xl font-semibold text-stone-dark">My Groups</h2>
        <div className="flex gap-2">
          <Dialog open={joinGroupOpen} onOpenChange={setJoinGroupOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full border-sage text-sage" data-testid="join-group-btn">
                Join
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif">Join a Group</DialogTitle>
                <DialogDescription>Enter the invite code shared with you</DialogDescription>
              </DialogHeader>
              <form onSubmit={joinGroup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="inviteCode">Invite Code</Label>
                  <Input
                    id="inviteCode"
                    placeholder="e.g. ABC12345"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    required
                    className="uppercase"
                    data-testid="invite-code-input"
                  />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    className="bg-sage hover:bg-sage-dark text-white rounded-full"
                    disabled={formLoading}
                    data-testid="submit-join-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Join Group"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={newGroupOpen} onOpenChange={setNewGroupOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-sage hover:bg-sage-dark text-white rounded-full" data-testid="create-group-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif">Create a Group</DialogTitle>
                <DialogDescription>Start a new prayer group</DialogDescription>
              </DialogHeader>
              <form onSubmit={createGroup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="groupName">Group Name</Label>
                  <Input
                    id="groupName"
                    placeholder="e.g. Family Prayers"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    required
                    data-testid="group-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="groupDesc">Description (optional)</Label>
                  <Textarea
                    id="groupDesc"
                    placeholder="What is this group about?"
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    data-testid="group-desc-input"
                  />
                </div>
                {churches.length > 0 && (
                  <div className="space-y-2">
                    <Label>Associate with Church (optional)</Label>
                    <Select
                      value={groupForm.church_id || "none"}
                      onValueChange={(value) => setGroupForm({ ...groupForm, church_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="group-church-select">
                        <SelectValue placeholder="No church association" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No church association</SelectItem>
                        {churches.map((church) => (
                          <SelectItem key={church.id} value={church.id}>
                            {church.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
                  </DialogClose>
                  <Button
                    type="submit"
                    className="bg-sage hover:bg-sage-dark text-white rounded-full"
                    disabled={formLoading}
                    data-testid="submit-group-btn"
                  >
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Group"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="space-y-3">
        {groups.length === 0 ? (
          <Card className="border-stone border-dashed">
            <CardContent className="p-6 text-center">
              <Users className="w-10 h-10 text-stone-dark/20 mx-auto mb-3" />
              <p className="text-stone-dark/60 text-sm">No groups yet</p>
              <p className="text-xs text-stone-dark/40 mt-1">Create or join a group to pray together</p>
            </CardContent>
          </Card>
        ) : (
          groups.map((group) => (
            <Link key={group.id} to={`/groups/${group.id}`}>
              <Card className="border-stone card-hover cursor-pointer" data-testid={`group-card-${group.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-stone-dark">{group.name}</h3>
                      {group.church_name && (
                        <p className="text-xs text-sage/70 flex items-center gap-1 mt-0.5">
                          <Church className="w-3 h-3" />
                          {group.church_name}
                        </p>
                      )}
                      <p className="text-xs text-stone-dark/60 mt-1">
                        {group.member_count} member{group.member_count !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2"
                        onClick={(e) => { e.preventDefault(); copyInviteCode(group.invite_code); }}
                        data-testid={`copy-invite-${group.id}`}
                      >
                        {copiedCode === group.invite_code ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-stone-dark/50" />
                        )}
                      </Button>
                      <ChevronRight className="w-4 h-4 text-stone-dark/30" />
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs bg-stone px-2 py-1 rounded font-mono">
                      {group.invite_code}
                    </span>
                    <span className="text-xs text-stone-dark/40">Invite code</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  );
}
